import usePageTitle from "../utils/usePageTitle";
import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Form, Formik, useField, type FormikHelpers } from "formik";
import { Pencil, Plus, Trash2 } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { TextAreaInput, TextInput } from "common-tailwind-components";

import {
  create_vocabulary_entry,
  delete_vocabulary_entry,
  get_vocabulary_catalogue,
  get_vocabulary_entries,
  splitApiRequestFormErrors,
  update_vocabulary_entry,
} from "../api";
import type {
  MacroProcess,
  VocabularyCatalogue,
  VocabularyEntryDb,
  VocabularyEntryForm,
  VocabularyScope,
} from "../api/types";
import Modal from "../components/Modal";
import Categories from "../components/vocabulary/Categories";
import {
  Badge,
  FieldLabel,
  ModalButtons,
  fieldClass,
  summaryClass,
} from "../components/vocabulary/ui";
import { useLogin } from "../reducers";
import { isAdmin } from "../utils/permissions";
import { normalizeName } from "../utils/vocabulary";

/** One definition of a process in the catalogue: the built-in one, or a
 * stored contribution. Definitions sharing a normalised name form one card. */
type Definition =
  | { kind: "builtin"; process: MacroProcess }
  | { kind: "entry"; entry: VocabularyEntryDb };

type VocabularyEntryFormValues = {
  process: string;
  steps: string;
  scope: VocabularyScope;
};

const vocabularyFormFields = ["process", "steps", "scope"] as const;

const vocabularyValuesToForm = (values: VocabularyEntryFormValues): VocabularyEntryForm => ({
  process: values.process,
  steps: values.steps
    .split("\n")
    .map(line => line.trim())
    .filter(Boolean),
  scope: values.scope,
});

const applyVocabularyFormError = (
  err: unknown,
  t: ReturnType<typeof useTranslation>["t"],
  helpers: Pick<
    FormikHelpers<VocabularyEntryFormValues>,
    "setErrors" | "setTouched" | "setSubmitting"
  >,
  setError: (error: string | null) => void,
) => {
  const parsed = splitApiRequestFormErrors<VocabularyEntryFormValues>(err, t, vocabularyFormFields);
  helpers.setTouched(parsed.touched, false);
  helpers.setErrors(parsed.fieldErrors);
  setError(parsed.remainder);
  helpers.setSubmitting(false);
};

const VocabularyScopeSelect = ({
  name,
  className,
  disabled,
}: {
  name: keyof VocabularyEntryFormValues;
  className: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const [field, meta] = useField<VocabularyScope>(name);
  const error = meta.touched && meta.error ? meta.error : null;
  return (
    <>
      <select
        {...field}
        className={clsx(className, error && "border-danger")}
        aria-label={t("vocabulary_scope_label")}
        aria-invalid={!!error}
        disabled={disabled}
      >
        <option value="user">{t("vocabulary_scope_user")}</option>
        <option value="company">{t("vocabulary_scope_company")}</option>
      </select>
      {error && <p className="text-sm text-danger">{error}</p>}
    </>
  );
};

const definitionName = (definition: Definition): string => {
  switch (definition.kind) {
    case "builtin":
      return definition.process.process;
    case "entry":
      return definition.entry.payload.v === "1" ? definition.entry.payload.process : "";
  }
};

const definitionSteps = (definition: Definition): string[] => {
  switch (definition.kind) {
    case "builtin":
      return definition.process.steps ?? [];
    case "entry":
      return definition.entry.payload.v === "1" ? (definition.entry.payload.steps ?? []) : [];
  }
};

const DefinitionBadge = ({ definition }: { definition: Definition }) => {
  const { t } = useTranslation();
  switch (definition.kind) {
    case "builtin":
      return <Badge label={t("vocabulary_scope_badge_builtin")} />;
    case "entry":
      return (
        <Badge
          label={
            definition.entry.payload.v === "1" && definition.entry.payload.scope === "company"
              ? t("vocabulary_scope_badge_company")
              : t("vocabulary_scope_badge_personal")
          }
        />
      );
  }
};

/** A process's steps, collapsed behind a "{n} steps" disclosure. */
const StepsDisclosure = ({ steps }: { steps: string[] }) => {
  const { t } = useTranslation();
  if (steps.length === 0) return null;
  return (
    <details className="mt-2">
      <summary className={summaryClass}>{t("n_steps", { count: steps.length })}</summary>
      <ul className="mt-1 space-y-0.5 text-sm text-text-mid">
        {steps.map(step => (
          <li key={step}>• {step}</li>
        ))}
      </ul>
    </details>
  );
};

/** The in-card editor: the full definition (name, steps, admin-only scope)
 * with save/cancel; Escape cancels. `onSave` failures stay in the card. */
const EntryEditor = ({
  initialProcess,
  initialSteps,
  initialScope,
  onSave,
  onCancel,
}: {
  initialProcess: string;
  initialSteps: string[];
  initialScope: VocabularyScope;
  onSave: (form: VocabularyEntryForm) => Promise<void>;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();
  const { permissions } = useLogin();
  const [error, setError] = useState<string | null>(null);

  const initialValues: VocabularyEntryFormValues = {
    process: initialProcess,
    steps: initialSteps.join("\n"),
    scope: initialScope,
  };

  const onSubmit = async (
    values: VocabularyEntryFormValues,
    helpers: FormikHelpers<VocabularyEntryFormValues>,
  ) => {
    setError(null);
    try {
      await onSave(vocabularyValuesToForm(values));
    } catch (err) {
      applyVocabularyFormError(err, t, helpers, setError);
    }
  };

  return (
    <Formik<VocabularyEntryFormValues> initialValues={initialValues} onSubmit={onSubmit}>
      {({ isSubmitting, values }) => (
        <Form
          onKeyDown={e => {
            if (e.key === "Escape") onCancel();
          }}
          className="space-y-2"
        >
          <TextInput
            name="process"
            wrapped={false}
            unstyled
            className="w-full rounded-md border border-outline bg-background px-2 py-1 text-sm text-text outline-none transition focus:border-primary"
            ariaLabel={t("vocabulary_process_name")}
            disabled={isSubmitting}
            required
          />
          <TextAreaInput
            name="steps"
            wrapped={false}
            unstyled
            className="min-h-24 w-full rounded-md border border-outline bg-background px-2 py-1 text-sm text-text outline-none transition focus:border-primary"
            placeholder={t("vocabulary_steps_hint")}
            disabled={isSubmitting}
          />
          {isAdmin(permissions) && (
            <VocabularyScopeSelect
              name="scope"
              className="w-full rounded-md border border-outline bg-background px-2 py-1 text-sm text-text"
              disabled={isSubmitting}
            />
          )}
          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="rounded-md border border-outline px-2.5 py-1 text-sm text-text-dim transition-colors hover:text-text"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !values.process.trim()}
              className="rounded-md bg-primary px-2.5 py-1 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("save")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

/** One catalogue process: the definition the caller's analyses will use on
 * top, with the definitions it overrides stacked (collapsed) below in the
 * same card. Editing edits the effective entry when the caller may touch it,
 * and otherwise creates their own override prefilled with it. */
const ProcessCard = ({
  definitions,
  onSaveEntry,
  onDeleteEntry,
  onCreateOverride,
}: {
  definitions: Definition[];
  onSaveEntry: (entryId: string, form: VocabularyEntryForm) => Promise<void>;
  onDeleteEntry: (entryId: string) => Promise<void>;
  onCreateOverride: (form: VocabularyEntryForm) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { user, permissions } = useLogin();
  const [editing, setEditing] = useState(false);

  const effective = definitions[0];
  const overridden = definitions.slice(1);
  const effectiveEntry = effective.kind === "entry" ? effective.entry : null;
  const canEditEffective =
    effectiveEntry !== null && (effectiveEntry.user_id === user.id || isAdmin(permissions));
  const canDelete = (entry: VocabularyEntryDb) => entry.user_id === user.id || isAdmin(permissions);

  if (editing) {
    const effectiveScope =
      effectiveEntry !== null && effectiveEntry.payload.v === "1"
        ? (effectiveEntry.payload.scope ?? "user")
        : "user";
    return (
      <div className="rounded-xl border border-outline bg-surface p-4">
        <EntryEditor
          initialProcess={definitionName(effective)}
          initialSteps={definitionSteps(effective)}
          initialScope={canEditEffective ? effectiveScope : "user"}
          onSave={async form => {
            if (canEditEffective && effectiveEntry !== null) {
              await onSaveEntry(effectiveEntry.id, form);
            } else {
              await onCreateOverride(form);
            }
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-outline bg-surface p-4">
      <div className="flex items-start justify-between gap-2">
        {/* min-h matches the tallest action control, so the header (and the
            steps disclosure under it) sits at the same height whether a card
            shows icon buttons, the override button, or nothing. */}
        <div className="flex min-h-8 min-w-0 flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-text">{definitionName(effective)}</span>
          <DefinitionBadge definition={effective} />
          {effectiveEntry !== null && effectiveEntry.user_id === user.id && (
            <Badge label={t("vocabulary_contributed_by_you")} />
          )}
        </div>
        <div className="flex min-h-8 shrink-0 items-center gap-1">
          {canEditEffective && effectiveEntry !== null ? (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                title={t("vocabulary_edit_entry")}
                aria-label={t("vocabulary_edit_entry")}
                className="rounded-md border border-outline p-1.5 text-text-dim transition-colors hover:text-text"
              >
                <Pencil size={16} />
              </button>
              <button
                type="button"
                onClick={() => void onDeleteEntry(effectiveEntry.id)}
                title={t("vocabulary_delete_entry")}
                aria-label={t("vocabulary_delete_entry")}
                className="rounded-md border border-outline p-1.5 text-text-dim transition-colors hover:border-danger/40 hover:text-danger"
              >
                <Trash2 size={16} />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-outline px-2 py-0.5 text-xs text-text-dim transition-colors hover:text-text"
            >
              {t("vocabulary_create_override")}
            </button>
          )}
        </div>
      </div>
      <StepsDisclosure steps={definitionSteps(effective)} />
      {overridden.length > 0 && (
        <details className="mt-3 border-t border-outline pt-2">
          <summary className={summaryClass}>
            {t("vocabulary_overridden_definitions", { count: overridden.length })}
          </summary>
          <div className="mt-2 space-y-3">
            {overridden.map(definition => (
              <div
                key={definition.kind === "entry" ? definition.entry.id : "builtin"}
                className="opacity-70"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-text">
                      {definitionName(definition)}
                    </span>
                    <DefinitionBadge definition={definition} />
                    {definition.kind === "entry" && definition.entry.user_id === user.id && (
                      <Badge label={t("vocabulary_contributed_by_you")} />
                    )}
                  </div>
                  {definition.kind === "entry" && canDelete(definition.entry) && (
                    <button
                      type="button"
                      onClick={() => void onDeleteEntry(definition.entry.id)}
                      title={t("vocabulary_delete_entry")}
                      aria-label={t("vocabulary_delete_entry")}
                      className="shrink-0 rounded-md border border-outline p-1 text-text-dim transition-colors hover:border-danger/40 hover:text-danger"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <StepsDisclosure steps={definitionSteps(definition)} />
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const Vocabulary = () => {
  const { t } = useTranslation();
  usePageTitle(t("vocabulary"));
  const { user, permissions } = useLogin();
  const [catalogue, setCatalogue] = useState<VocabularyCatalogue | null>(null);
  const [entries, setEntries] = useState<VocabularyEntryDb[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [nextCatalogue, nextEntries] = await Promise.all([
      get_vocabulary_catalogue(),
      get_vocabulary_entries(),
    ]);
    setCatalogue(nextCatalogue);
    setEntries(nextEntries);
  }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .then(() => {
        if (!cancelled) setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const onSubmit = async (
    values: VocabularyEntryFormValues,
    helpers: FormikHelpers<VocabularyEntryFormValues>,
  ) => {
    setFormError(null);
    try {
      await create_vocabulary_entry(vocabularyValuesToForm(values));
      helpers.resetForm();
      setAdding(false);
      await load();
    } catch (err) {
      applyVocabularyFormError(err, t, helpers, setFormError);
    }
  };

  const onDelete = async (entryId: string) => {
    try {
      await delete_vocabulary_entry(entryId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const saveEntry = async (entryId: string, form: VocabularyEntryForm) => {
    await update_vocabulary_entry(entryId, form);
    await load();
  };

  const createOverride = async (form: VocabularyEntryForm) => {
    await create_vocabulary_entry(form);
    await load();
  };

  const v1Entries = (entries ?? []).filter(entry => entry.payload.v === "1");

  // One card per normalised name, its definitions ranked by what the
  // caller's analyses would use: own entry, then company-wide entries, then
  // the built-in one, then other users' personal entries (visible only to
  // vocabulary managers, and applying to their owners alone).
  const rank = (definition: Definition): number => {
    switch (definition.kind) {
      case "entry":
        if (definition.entry.user_id === user.id) return 0;
        return definition.entry.payload.v === "1" && definition.entry.payload.scope === "company"
          ? 1
          : 3;
      case "builtin":
        return 2;
    }
  };
  const groups = new Map<string, Definition[]>();
  for (const process of catalogue?.base ?? []) {
    const key = normalizeName(process.process);
    groups.set(key, [...(groups.get(key) ?? []), { kind: "builtin", process }]);
  }
  for (const entry of v1Entries) {
    const key = entry.normalised_process_name;
    groups.set(key, [...(groups.get(key) ?? []), { kind: "entry", entry }]);
  }
  const cards = [...groups.entries()]
    .map(([key, definitions]) => ({
      key,
      definitions: definitions.sort((a, b) => rank(a) - rank(b)),
    }))
    .sort((a, b) =>
      definitionName(a.definitions[0]).localeCompare(definitionName(b.definitions[0])),
    );

  return (
    <div className="min-h-screen px-6 pb-16 pt-20">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-text">{t("vocabulary")}</h1>
        <p className="mt-2 text-sm text-text-mid">{t("vocabulary_page_description")}</p>
      </header>

      {error && (
        <div className="mb-6 rounded-xl border border-danger/30 bg-danger/10 p-4 text-sm text-danger-strong">
          {t("failed_to_load_vocabulary")}: {error}
        </div>
      )}

      {!catalogue && !error && <div className="text-sm text-text-dim">{t("loading_ellipsis")}</div>}

      {catalogue && (
        <section>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-lg font-medium text-text">{t("vocabulary_processes")}</h2>
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-outline-strong bg-surface px-4 py-2 text-sm font-medium text-text shadow-sm transition hover:border-primary hover:text-primary hover:shadow-md"
            >
              <Plus size={16} />
              {t("vocabulary_new_entry")}
            </button>
          </div>
          {cards.length === 0 && (
            <p className="mb-3 text-sm text-text-dim">{t("vocabulary_empty")}</p>
          )}
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {cards.map(card => (
              <ProcessCard
                key={card.key}
                definitions={card.definitions}
                onSaveEntry={saveEntry}
                onDeleteEntry={onDelete}
                onCreateOverride={createOverride}
              />
            ))}
          </div>
        </section>
      )}

      <Formik<VocabularyEntryFormValues>
        initialValues={{ process: "", steps: "", scope: "user" }}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, resetForm }) => (
          <Modal
            open={adding}
            onOpenChange={nextOpen => {
              if (!nextOpen) {
                resetForm();
                setFormError(null);
              }
              setAdding(nextOpen);
            }}
            busy={isSubmitting}
          >
            <Dialog.Title className="pr-10 text-lg font-semibold text-text">
              {t("vocabulary_new_entry")}
            </Dialog.Title>
            <Form className="mt-5 space-y-3">
              <FieldLabel label={t("vocabulary_process_name")}>
                <TextInput
                  name="process"
                  wrapped={false}
                  unstyled
                  className={fieldClass}
                  disabled={isSubmitting}
                  required
                />
              </FieldLabel>
              <FieldLabel label={t("vocabulary_steps_hint")}>
                <TextAreaInput
                  name="steps"
                  wrapped={false}
                  unstyled
                  className={`min-h-24 ${fieldClass}`}
                  disabled={isSubmitting}
                />
              </FieldLabel>
              {isAdmin(permissions) && (
                <FieldLabel label={t("vocabulary_scope_label")}>
                  <VocabularyScopeSelect
                    name="scope"
                    className={fieldClass}
                    disabled={isSubmitting}
                  />
                </FieldLabel>
              )}
              {formError && <p className="text-sm text-danger">{formError}</p>}
              <ModalButtons
                submitLabel={
                  isSubmitting ? t("vocabulary_adding_entry") : t("vocabulary_add_entry")
                }
                disabled={isSubmitting}
                submitDisabled={!values.process.trim()}
              />
            </Form>
          </Modal>
        )}
      </Formik>

      <Categories />
    </div>
  );
};

export default Vocabulary;
